/**
 * CategoryOverview Component
 * Displays category overview table in impact assessment
 */

import React, { useState, useEffect, useCallback, useRef, Component } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { appDatePickerSlots } from "../../shared/datePickerSlots";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import {
  DataGridPremium,
  GridColDef,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { apiClient } from "../../../services/apiClient";
import { getCategoryOverviewFilterOptions } from "../../../services/regulationsService";
import {
  formatFilterOptionDisplayName,
  pickFilterOptionQueryValue,
} from "../../../utils/assessmentHistoryStatus";

interface OverviewFilterOption {
  name: string;
  value: string;
}

interface OverviewFilterConfig {
  name: string;
  options: OverviewFilterOption[];
}

const FIELD_MAPPING: Record<string, string> = {
  directImpact: "impacted_level",
  date: "updated_at",
};

function isFilterValueValid(value: unknown): boolean {
  return value !== undefined && value !== null && value !== "";
}

function buildOverviewQueryParams(
  sortModel: GridSortModel,
  paginationModel: { page: number; pageSize: number },
  filters: {
    dimensions: string;
    concept: string;
    impactLevel: string;
    date: Dayjs | null;
  }
): URLSearchParams {
  const params = new URLSearchParams();
  params.append("page", String(paginationModel.page + 1));
  params.append("page_size", String(paginationModel.pageSize));
  
  if (sortModel.length > 0) {
    const frontendField = sortModel[0].field;
    const backendField = FIELD_MAPPING[frontendField] ?? frontendField;
    params.append("sort_by", backendField);
    params.append("sort_order", sortModel[0].sort ?? "asc");
  }

  // Add explicit filters according to API spec
  if (filters.dimensions) params.append("concept_name", filters.dimensions);
  if (filters.concept) params.append("concept_value_name", filters.concept);
  
  if (filters.impactLevel) {
    params.append("impacted_level", filters.impactLevel);
  }
  
  if (filters.date) params.append("filter_date", filters.date.format("YYYY-MM-DD"));

  return params;
}

function extractDataFromResponse(response: { status?: string; data?: unknown }): unknown[] {
  if (response.status !== "success") return [];
  const data = response.data;
  if (Array.isArray(data)) return data;
  const items = (data as { items?: unknown[] })?.items;
  return Array.isArray(items) ? items : [];
}

function objectToDisplayString(obj: object): string {
  const o = obj as { description?: string; name?: string; code?: string };
  return o.description ?? o.name ?? o.code ?? JSON.stringify(obj);
}

function safeStringify(value: unknown): string {
  if (!value) return "";
  if (typeof value === "object" && value !== null) return objectToDisplayString(value);
  return String(value);
}

function formatDateForDisplay(isoString: string): string {
  if (!isoString || typeof isoString !== "string") return "";
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;
  const months = [
    "Jan","Feb","Mar","Apr","May","Jun",
    "Jul","Aug","Sep","Oct","Nov","Dec",
  ];
  const day = date.getDate().toString().padStart(2, "0");
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

interface CategoryRow {
  id: number;
  task_type: string;
  concept_name: string;
  concept_value_name: string;
  directImpact: string;
  impactOnRelatedConcepts: string | string[];
  date: string;
}

function transformApiItemToRow(
  item: Record<string, unknown>,
  index: number,
): CategoryRow | null {
  try {
    const dateValue = (item.updated_at ?? item.date) as string | undefined;
    const formattedDate = dateValue ? formatDateForDisplay(dateValue) : "";

    let directImpact = "";
    if (item.impacted_level !== undefined && item.impacted_level !== null) {
      directImpact = String(item.impacted_level);
    } else if (item.impact_level !== undefined && item.impact_level !== null) {
      directImpact = String(item.impact_level);
    } else if (item.impacted !== undefined) {
      directImpact = item.impacted ? "High" : "No";
    } else {
      directImpact = (item.direct_impact ?? item.directImpact ?? "") as string;
    }

    let impactOnRelatedConcepts: string | string[] = "N/A";
    const related = item.impacted_related_concepts as Array<{ name?: string }> | undefined;
    if (Array.isArray(related) && related.length > 0) {
      const names = related.map((c) => c?.name).filter(Boolean) as string[];
      impactOnRelatedConcepts = names.length > 0 ? names : "N/A";
    }

    return {
      id: index + 1,
      task_type: safeStringify(item.task_type),
      concept_name: safeStringify(item.concept_name),
      concept_value_name: safeStringify(item.concept_value_name),
      directImpact,
      impactOnRelatedConcepts,
      date: formattedDate,
    };
  } catch {
    return null;
  }
}

// Error Boundary to catch rendering errors
class ErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("CategoryOverview Error Boundary caught an error:", error);
    console.error("Error Info:", errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="error">
          <Box>
            <strong>
              An error occurred while rendering the category overview:
            </strong>
            <Box sx={{ mt: 1 }}>
              {this.state.error?.message || "Unknown error"}
            </Box>
            <Box sx={{ mt: 2, fontSize: (theme) => theme.typography.body2.fontSize, color: "#6b7280" }}>
              Please check the console for more details.
            </Box>
          </Box>
        </Alert>
      );
    }

    return this.props.children;
  }
}

interface CategoryOverviewProps {
  regulationId: string;
  regulationVersion: string;
}

function safeRenderCell(value: unknown): string {
  if (!value) return "-";
  if (typeof value === "object" && value !== null) return objectToDisplayString(value);
  const stringValue = String(value).trim();
  return stringValue !== "" ? stringValue : "-";
}

function renderCapitalizedCell(value: unknown): string {
  const str = safeRenderCell(value);
  if (str === "-") return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const IMPACT_LEVEL_STYLES: Record<string, { backgroundColor: string; color: string }> = {
  high:   { backgroundColor: "#fee2e2", color: "#991b1b" },
  medium: { backgroundColor: "#ffedd5", color: "#9a3412" },
  low:    { backgroundColor: "#fef9c3", color: "#854d0e" },
  no:     { backgroundColor: "#dcfce7", color: "#166534" },
  High:   { backgroundColor: "#fee2e2", color: "#991b1b" },
  Medium: { backgroundColor: "#ffedd5", color: "#9a3412" },
  Low:    { backgroundColor: "#fef9c3", color: "#854d0e" },
  No:     { backgroundColor: "#dcfce7", color: "#166534" },
};

function renderImpactLevelCell(value: unknown): React.ReactNode {
  if (!value || value === "-") return <span>-</span>;
  const str = typeof value === "object" && value !== null ? objectToDisplayString(value) : String(value);
  const capitalized = str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  const lower = str.toLowerCase();
  const style = IMPACT_LEVEL_STYLES[lower];
  if (style) {
    return (
      <Chip
        label={capitalized}
        size="small"
        sx={{
          ...style,
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "24px",
          "& .MuiChip-label": { px: 1.5 },
        }}
      />
    );
  }
  return <span>{capitalized}</span>;
}

function renderImpactOnRelatedCell(value: unknown): React.ReactNode {
  if (typeof value === "string") {
    return <Box component="span" sx={{ color: "#6b7280" }}>{value}</Box>;
  }
  if (Array.isArray(value) && value.length > 0) {
    return (
      <Box sx={{ py: 0.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
        {value.map((name, index) => (
          <Box
            key={`concept-${index}-${name}`}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.5,
              fontSize: (theme) => theme.typography.body2.fontSize,
            }}
          >
            <span style={{ marginTop: "2px" }}>•</span>
            <span>{name}</span>
          </Box>
        ))}
      </Box>
    );
  }
  return <Box component="span" sx={{ color: "#6b7280" }}>N/A</Box>;
}

/** Fixed viewport height — body scrolls inside the grid regardless of page size. */
const CATEGORY_OVERVIEW_GRID_HEIGHT = 480;

const categoryColumns: GridColDef<CategoryRow>[] = [
  {
    field: "concept_name",
    headerName: "Dimension",
    flex: 1,
    minWidth: 150,
    sortable: true,
    renderCell: (params) => renderCapitalizedCell(params.value),
  },
  {
    field: "concept_value_name",
    headerName: "Value",
    flex: 1,
    minWidth: 150,
    sortable: true,
    renderCell: (params) => renderCapitalizedCell(params.value),
  },
  {
    field: "directImpact",
    headerName: "Impact Level",
    flex: 1,
    minWidth: 120,
    sortable: true,
    renderCell: (params) => renderImpactLevelCell(params.value),
  },
  {
    field: "impactOnRelatedConcepts",
    headerName: "Impact on related values",
    flex: 1.5,
    minWidth: 200,
    sortable: false,
    renderCell: (params) => renderImpactOnRelatedCell(params.value),
  },
  {
    field: "date",
    headerName: "Date",
    flex: 1,
    minWidth: 120,
    sortable: true,
    renderCell: (params) => safeRenderCell(params.value),
  },
];

const CategoryOverviewInner: React.FC<CategoryOverviewProps> = ({
  regulationId,
  regulationVersion,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryData, setCategoryData] = useState<CategoryRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  
  // Filters state
  const [dimensions, setDimensions] = useState("");
  const [concept, setConcept] = useState("");
  const [impactLevel, setImpactLevel] = useState("");
  const [date, setDate] = useState<Dayjs | null>(null);
  const [filterOptions, setFilterOptions] = useState<OverviewFilterConfig[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);

  // Guard to prevent DataGrid from auto-resetting page to 0 on sort/filter change
  const keepPageRef = useRef(false);

  const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
    keepPageRef.current = true;
    setSortModel(newSortModel);
  }, []);

  const handlePaginationModelChange = useCallback(
    (newModel: { page: number; pageSize: number }) => {
      setLoading(true);
      if (keepPageRef.current) {
        keepPageRef.current = false;
        setPaginationModel((prev) => ({ ...prev, pageSize: newModel.pageSize }));
        return;
      }
      setPaginationModel(newModel);
    },
    [],
  );

  // Fetch filter options (Dimension / Value / Impact level are handled here)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        setFilterLoading(true);
        const response = await getCategoryOverviewFilterOptions(regulationId, regulationVersion);
        if (response.status === "success" && response.data) {
          setFilterOptions(response.data as OverviewFilterConfig[]);
        }
      } catch (err) {
        console.error("Error fetching filter options:", err);
      } finally {
        setFilterLoading(false);
      }
    };
    fetchFilterOptions();
  }, [regulationId, regulationVersion]);

  useEffect(() => {
    const fetchCategoryOverview = async () => {
      if (!regulationId || !regulationVersion) return;

      try {
        setLoading(true);
        setError(null);

        const queryParams = buildOverviewQueryParams(sortModel, paginationModel, {
          dimensions,
          concept,
          impactLevel,
          date
        });
        const queryString = queryParams.toString();
        const url = `/regulations/${regulationId}/${regulationVersion}/assessments/overview${
          queryString ? `?${queryString}` : ""
        }`;

        const response = await apiClient.get(url);
        const dataToTransform = extractDataFromResponse(response);
        const total =
          (response as unknown as { total?: number })?.total ?? dataToTransform.length;

        const transformedData = dataToTransform
          .map((item, index) =>
            transformApiItemToRow(
              item as Record<string, unknown>,
              paginationModel.page * paginationModel.pageSize + index,
            ),
          )
          .filter((row): row is CategoryRow => row != null);

        setCategoryData(transformedData);
        setTotalRows(total);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load category overview",
        );
        setCategoryData([]);
        // Preserve totalRows on transport/API errors.
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryOverview();
  }, [regulationId, regulationVersion, sortModel, paginationModel, dimensions, concept, impactLevel, date]);

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters Bar */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <TextField
          select
          label="Dimension"
          value={dimensions}
          onChange={(e) => setDimensions(e.target.value)}
          sx={{ minWidth: 200 }}
          size="small"
          disabled={loading || filterLoading}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            },
          }}
        >
          <MenuItem value="">All</MenuItem>
          {filterOptions.find((f) => f.name === "concept_name")?.options.map((opt) => {
            const queryValue = pickFilterOptionQueryValue(opt);
            return (
              <MenuItem key={queryValue} value={queryValue}>
                {formatFilterOptionDisplayName(opt.name)}
              </MenuItem>
            );
          })}
        </TextField>

        <Autocomplete
          size="small"
          options={filterOptions.find((f) => f.name === "concept_value_name")?.options ?? []}
          getOptionLabel={(option) => formatFilterOptionDisplayName(option.name)}
          value={
            filterOptions
              .find((f) => f.name === "concept_value_name")
              ?.options.find((opt) => pickFilterOptionQueryValue(opt) === concept) ?? null
          }
          onChange={(_, newValue) => {
            setConcept(newValue ? pickFilterOptionQueryValue(newValue) : "");
          }}
          isOptionEqualToValue={(option, value) =>
            pickFilterOptionQueryValue(option) === pickFilterOptionQueryValue(value)
          }
          sx={{ minWidth: 250 }}
          disabled={loading || filterLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Value"
              placeholder="Search values..."
            />
          )}
          ListboxProps={{
            style: {
              maxHeight: 300,
            },
          }}
        />

        <TextField
          select
          label="Impact Level"
          value={impactLevel}
          onChange={(e) => setImpactLevel(e.target.value)}
          sx={{ minWidth: 200 }}
          size="small"
          disabled={loading || filterLoading}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            },
          }}
        >
          <MenuItem value="">All</MenuItem>
          {(() => {
            const options = filterOptions.find((f) => f.name === "impacted_level")?.options;
            if (options && options.length > 0) {
              return options.map((opt) => {
                const queryValue = pickFilterOptionQueryValue(opt);
                return (
                  <MenuItem key={queryValue} value={queryValue}>
                    {formatFilterOptionDisplayName(opt.name)}
                  </MenuItem>
                );
              });
            }
            // Fallback hardcoded options
            return (
              [
                { label: "High", value: "high" },
                { label: "Medium", value: "medium" },
                { label: "Low", value: "low" },
                { label: "No", value: "no" },
              ] as const
            ).map((lv) => (
              <MenuItem key={lv.value} value={lv.value}>
                {lv.label}
              </MenuItem>
            ));
          })()}
        </TextField>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date"
            value={date}
            format="DD-MMM-YYYY"
            onChange={(newValue) => setDate(newValue)}
            disabled={loading}
            slots={appDatePickerSlots}
            slotProps={{
              textField: {
                size: "small",
                sx: { minWidth: 200 },
              },
              field: { clearable: true, onClear: () => setDate(null) },
            }}
          />
        </LocalizationProvider>
      </Box>

      {/* Category Overview table — fixed height; virtual scroller shows scrollbar */}
      <Box
        sx={{
          width: "100%",
          height: CATEGORY_OVERVIEW_GRID_HEIGHT,
          minHeight: CATEGORY_OVERVIEW_GRID_HEIGHT,
          maxHeight: CATEGORY_OVERVIEW_GRID_HEIGHT,
        }}
      >
        <DataGridPremium
          rows={categoryData || []}
          columns={categoryColumns}
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          disableRowSelectionOnClick
          disableColumnMenu
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel}
          pageSizeOptions={[10, 20, 50, 100]}
          getRowHeight={() => "auto"}
          sx={{
            height: "100%",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            "& .MuiDataGrid-main": {
              overflow: "hidden",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflow: "auto",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f9fafb",
              borderBottom: "1px solid #e5e7eb",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              fontSize: (theme) => theme.typography.body2.fontSize,
              color: "#374151",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f3f4f6",
              fontSize: (theme) => theme.typography.body2.fontSize,
              py: 1.5,
            },
            "& .MuiDataGrid-row": {
              minHeight: "52px !important",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f9fafb",
            },
            "& .MuiDataGrid-columnSeparator": {
              display: "none",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #e5e7eb",
            },
          }}
        />
      </Box>
    </Box>
  );
};

// Export wrapped component with error boundary
export const CategoryOverview: React.FC<CategoryOverviewProps> = (props) => {
  return (
    <ErrorBoundary>
      <CategoryOverviewInner {...props} />
    </ErrorBoundary>
  );
};
